import React from 'react';
import { Card, Button } from '../BossUI';

const LoginPage = () => {
  const apiUrl = process.env.REACT_APP_API_URL;

  const handleGoogleLogin = () => {
    window.location.href = `${apiUrl}/auth/login/google`;
  };

  const handleMicrosoftLogin = () => {
    window.location.href = `${apiUrl}/auth/login/microsoft`;
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header Section */}
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img
                src="/hh.jpg"
                alt="Helping Harmony Logo"
                className="h-12 w-12 rounded-lg"
              />
              <h1 className="ml-3 text-xl font-bold text-neutral-900">
                Helping Harmony Online
              </h1>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:grid lg:grid-cols-2 gap-12 items-start">
          {/* Login Card - Shows first on mobile, second on desktop */}
          <div className="w-full order-1 lg:order-2 lg:ml-auto lg:max-w-md">
            <Card className="p-8">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-neutral-900">
                  Sign In
                </h2>
                <p className="mt-2 text-sm text-neutral-600">
                  Use your organization account to access the platform
                </p>
              </div>

              <div className="space-y-4">
                <Button
                  variant="outline"
                  size="lg"
                  className="w-full"
                  onClick={handleGoogleLogin}
                >
                  <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  Sign in with Google
                </Button>

                <Button
                  variant="outline"
                  size="lg"
                  className="w-full"
                  onClick={handleMicrosoftLogin}
                >
                  <svg className="h-5 w-5 mr-2" viewBox="0 0 23 23">
                    <path fill="#f3f3f3" d="M0 0h23v23H0z"/>
                    <path fill="#f35325" d="M1 1h10v10H1z"/>
                    <path fill="#81bc06" d="M12 1h10v10H12z"/>
                    <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                    <path fill="#ffba08" d="M12 12h10v10H12z"/>
                  </svg>
                  Sign in with Microsoft
                </Button>
              </div>

              <div className="mt-6 text-center text-sm text-neutral-600">
                <p>
                  By signing in, you agree to our{' '}
                  <a 
                    href="/tos.html" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    Terms of Service
                  </a>
                  {' '}and{' '}
                  <a 
                    href="/privacy.html" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    Privacy Policy
                  </a>
                </p>
              </div>
            </Card>
          </div>

          {/* Information Section - Shows second on mobile, first on desktop */}
          <div className="space-y-8 order-2 lg:order-1">
            <div>
              <h2 className="text-3xl font-bold text-neutral-900 tracking-tight sm:text-4xl">
                Music Therapy Session Management Platform
              </h2>
              <p className="mt-4 text-lg text-neutral-600">
                A comprehensive platform for music therapists to manage sessions, track progress, and maintain documentation for veteran music therapy programs.
              </p>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-neutral-900">Key Features</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-green-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="ml-3 text-neutral-600">Secure session documentation and reporting</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-green-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="ml-3 text-neutral-600">Student progress tracking and assessments</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-green-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="ml-3 text-neutral-600">Integrated file management for session materials</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-green-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="ml-3 text-neutral-600">Administrative tools for program management</span>
                </li>
              </ul>
            </div>

            <div>
              <p className="text-neutral-600">
                This platform is exclusively for authorized Helping Harmony Music Therapy staff and contracted therapists. Access requires authentication through your organization's email account.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-neutral-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-sm text-neutral-600">
            <p>© {new Date().getFullYear()} Helping Harmony Music Therapy LLC. All rights reserved.</p>
            <p className="mt-2">
              For support, please contact your program administrator.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LoginPage;